import React, { useCallback, useRef, useState, useMemo } from 'react';
import { Text, View, Pressable, TouchableOpacity, ScrollView, ActivityIndicator } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { LegendList } from '@legendapp/list';
import Avatar from '@/components/People/Avatar';
import Unicon from '@/components/General/Unicon';
import {Tooltip} from '@/components/General/Tooltip';
import DropdownMenu from '@/components/General/DropdownMenu';
import CheckinDetailsModal from './CheckinDetailsModal';

const FAMILY_AVATAR_SIZE = 32;

const CheckinCardGrid = ({ data, onPersonPress, onFamilyPress, onCheckOut, onReprint, listColumns, isFetching, hasNextPage, fetchNextPage, ListFooter }) => {
    const [detailsModalOpen, setDetailsModalOpen] = useState(false);
    const [selectedPersonId, setSelectedPersonId] = useState(null);
    const onPersonPressRef = useRef(onPersonPress);
    onPersonPressRef.current = onPersonPress;

    const onFamilyPressRef = useRef(onFamilyPress);
    onFamilyPressRef.current = onFamilyPress;

    const onCheckOutRef = useRef(onCheckOut);
    onCheckOutRef.current = onCheckOut;

    const onReprintRef = useRef(onReprint);
    onReprintRef.current = onReprint;

    // Get live person data from the data array based on selected ID
    const selectedPerson = useMemo(() => {
        return data.find(p => p.id === selectedPersonId) || null;
    }, [data, selectedPersonId]);

    const handleDetailsPress = (person) => {
        setSelectedPersonId(person.id);
        setDetailsModalOpen(true);
    };

    const handleModalClose = () => {
        setDetailsModalOpen(false);
        setSelectedPersonId(null);
    };

    const renderItem = useCallback(({ item: person }) => {
        const familyMembers = person?.Family?.FamilyMembers?.filter(p => p.id != person?.id) || [];
        const otherHouseholdMembers = person?.Address?.People?.filter(p => p.id != person?.id && !familyMembers.some(fm => fm.id === p.id)) || [];
        const hasFamily = familyMembers.length > 0 || otherHouseholdMembers.length > 0;
        const selected = person.isSelected || person.isPending;
        const selectedColor = person.isPending ? "warning" : "success";
        const hasUncheckedFamily = familyMembers.some(p => !p.isSelected) || otherHouseholdMembers.some(p => !p.isSelected) || !selected;

        return (
            <View testID={`CheckinCard-${person?.firstName} ${person?.lastName}`} style={styles.listItem}>
                <Pressable
                    testID="Pressable"
                    hitSlop={5}
                    onPress={() => onPersonPressRef.current?.(person)}
                    style={styles.card(hasFamily, selected, selectedColor)}
                >
                    <View style={styles.row}>
                        <Avatar borderGap disableBorder size={35} profile={person} />
                        <View style={styles.nameContainer}>
                            <Text style={styles.nameText} numberOfLines={1}>{person?.firstName} {person?.lastName}</Text>
                            {person.isSelected && person.subtitle ? (
                                <Text numberOfLines={1} style={styles.subtitleText}>{person.subtitle}</Text>
                            ) : null}
                        </View>
                    </View>

                    <View style={styles.trailingRow}>
                        {person.isProcessing ? (
                            <ActivityIndicator />
                        ) : selected ? (
                            <>
                                {person.isCheckedOut && (
                                    <Unicon testID="exit" name="exit" size={28} colorVariant="success" />
                                )}
                                <Unicon testID="checkmark-circle" name="checkmark-circle" size={28} colorVariant={selectedColor} />
                            </>
                        ) : (
                            <Unicon testID="ellipse-outline" name="ellipse-outline" size={28} colorVariant="textSecondary" />
                        )}
                    </View>
                </Pressable>

                <View style={styles.familyContainer(hasFamily)}>
                    {hasFamily ? (
                        <View style={styles.familyMembersContainer}>
                            {familyMembers.length > 0 && (
                                <View style={styles.familySection}>
                                    <Text style={styles.familySectionLabel}>Family</Text>
                                    <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.avatarScrollContent} keyboardShouldPersistTaps="handled"
>
                                        {familyMembers.map(item => (
                                            <View key={item.id} style={styles.avatarItem}>
                                                <Avatar
                                                    onPress={() => onFamilyPressRef.current?.(item, person)}
                                                    size={FAMILY_AVATAR_SIZE}
                                                    profile={item}
                                                    disableBorder
                                                    tooltipExtraText={item.selectionDetail}
                                                />
                                                {item.isProcessing && (
                                                    <View style={styles.avatarLoadingOverlay}>
                                                        <ActivityIndicator size="small" />
                                                    </View>
                                                )}
                                                {(item.isSelected || item.isPending) && (
                                                    <View style={styles.avatarBadge}>
                                                        <Unicon name={item.isCheckedOut ? "exit" : "checkmark-circle"} size={14} colorVariant={item.isPending ? "warning" : "success"} />
                                                    </View>
                                                )}
                                            </View>
                                        ))}
                                    </ScrollView>
                                </View>
                            )}

                            {(familyMembers.length > 0 && otherHouseholdMembers.length > 0) && <View style={styles.separator} />}

                            {otherHouseholdMembers.length > 0 && (
                                <View style={styles.familySection}>
                                    <Text style={styles.familySectionLabel}>Other Household Members</Text>
                                    <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.avatarScrollContent} keyboardShouldPersistTaps="handled"
>
                                        {otherHouseholdMembers.map(item => (
                                            <View key={item.id} style={styles.avatarItem}>
                                                <Avatar
                                                    onPress={() => onFamilyPressRef.current?.(item, person)}
                                                    size={FAMILY_AVATAR_SIZE}
                                                    profile={item}
                                                    disableBorder
                                                    tooltipExtraText={item.selectionDetail}
                                                />
                                                {item.isProcessing && (
                                                    <View style={styles.avatarLoadingOverlay}>
                                                        <ActivityIndicator size="small" />
                                                    </View>
                                                )}
                                                {(item.isSelected || item.isPending) && (
                                                    <View style={styles.avatarBadge}>
                                                        <Unicon style={{pointerEvents: 'none'}} name={item.isCheckedOut ? "exit" : "checkmark-circle"} size={14} colorVariant={item.isPending ? "warning" : "success"} />
                                                    </View>
                                                )}
                                            </View>
                                        ))}
                                    </ScrollView>
                                </View>
                            )}
                        </View>
                    ) : (
                        <View style={styles.noFamilyContainer}>
                            <Text style={styles.noFamilyText}>No family members</Text>
                        </View>
                    )}

                    {onFamilyPressRef.current && hasUncheckedFamily && (
                        <TouchableOpacity
                            onPress={() => {
                                if (person.isProcessing) return;
                                if (!selected) onPersonPressRef.current?.(person);
                                familyMembers.forEach(p => { if (!p.isSelected) onFamilyPressRef.current?.(p, person); });
                                otherHouseholdMembers.forEach(p => { if (!p.isSelected) onFamilyPressRef.current?.(p, person); });
                            }}
                            style={styles.checkAllInButton}
                        >
                            <Unicon name="checkmark-done" size={18} colorVariant="text" />
                        </TouchableOpacity>
                    )}
                </View>

                {/* Actions Section - Third section below family */}
                <View style={styles.actionsSection}>
                    <View style={styles.actionsRow}>
                        {selected && !person.isCheckedOut && !person.isPending && (
                            <Tooltip testID="CheckOutButton" title="Check Out" style={styles.actionButton} onPress={(e) => { e.stopPropagation(); onCheckOutRef.current?.(person); }}>
                                <Unicon name="exit" size={17} colorVariant="primary" />
                                <Text style={styles.actionText}>Check Out</Text>
                            </Tooltip>
                        )}
                        {onReprintRef.current && selected && !person.isCheckedOut && (
                            <DropdownMenu
                                options={[
                                    { name: "Individual Only", onSelect: () => onReprintRef.current(person, 'individual') },
                                    { name: "Entire Family", onSelect: () => onReprintRef.current(person, 'family') },
                                ]}
                                triggerComponent={(
                                    <View testID="ReprintButton" style={styles.actionButton}>
                                        <Unicon name="print" size={17} colorVariant="primary" />
                                        <Text style={styles.actionText}>Reprint</Text>
                                    </View>
                                )}
                            />
                        )}
                        <Tooltip testID="ViewDetailsButton" title="View Details" style={styles.actionButton} onPress={(e) => { e.stopPropagation(); handleDetailsPress(person); }}>
                            <Unicon name="information-circle" size={17} colorVariant="primary" />
                            <Text style={styles.actionText}>View Details</Text>
                        </Tooltip>
                    </View>
                </View>
            </View>
        );
    }, []);

    return (
        <View style={styles.listContainer}>
            <LegendList
                data={data}
                renderItem={renderItem}
                keyExtractor={(item) => item.id}
                estimatedItemSize={350}
                numColumns={listColumns}
                contentContainerStyle={styles.listContentContainer}
                onEndReached={() => {
                    if (!isFetching && hasNextPage) fetchNextPage();
                }}
                onEndReachedThreshold={0.5}
                ListFooterComponent={ListFooter}
                keyboardShouldPersistTaps="handled"
            />
            <CheckinDetailsModal
                isOpen={detailsModalOpen}
                onClose={handleModalClose}
                person={selectedPerson}
                onCheckIn={onPersonPress}
                onCheckOut={onCheckOut}
            />
        </View>
    );
};

const styles = StyleSheet.create(({ colors, fonts }) => ({
    listContainer: {
        flex: 1,
        paddingHorizontal: 8,
    },
    scrollView: {
        flex: 1,
    },
    listContentContainer: {
        paddingBottom: 30,
        width: '100%',
        alignSelf: 'center',
    },
    grid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
    },
    gridItem: (columns) => ({
        width: columns > 1 ? `${100 / columns}%` : '100%',
    }),
    listItem: {
        width: '100%',
        paddingBottom: 12,
        paddingHorizontal: 6,
    },
    card: (_hasFamily, selected, _selectedColor) => ({
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 15,
        paddingVertical: 10,
        borderRadius: 10,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        backgroundColor: colors.surface,
        justifyContent: 'space-between',
        boxShadow: '0px -2px 8px -5px rgba(0, 0, 0, 0.15)',
        borderColor: colors.surface,
        borderWidth: selected ? 2 : 0,
        borderBottomWidth: 0,
    }),
    row: {
        flexDirection: 'row',
        alignItems: 'center',
        flex: 1,
        minWidth: 0,
    },
    trailingRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        flexShrink: 0,
    },
    nameContainer: {
        paddingLeft: 12,
        flex: 1,
        minWidth: 0,
    },
    nameText: {
        fontSize: 14,
        fontFamily: fonts.heading,
        color: colors.text,
    },
    subtitleText: {
        fontSize: 12,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        fontStyle: 'italic',
    },
    reprintButton: {
        width: 32,
        height: 32,
        borderRadius: 8,
        backgroundColor: colors.background,
        alignItems: 'center',
        justifyContent: 'center',
    },
    detailsButton: {
        width: 32,
        height: 32,
        borderRadius: 8,
        backgroundColor: colors.background,
        alignItems: 'center',
        justifyContent: 'center',
    },
    familyContainer: (_hasFamily) => ({
        height: 65,
        backgroundColor: colors.surface,
        marginTop: 0,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        alignItems: 'center',
        flexDirection: 'row',
        borderTopColor: colors.background,
        borderTopWidth: 1,
        width: '100%',
        paddingRight: 4,
        boxShadow: '0px 2px 8px -5px rgba(0, 0, 0, 0.15)',
    }),
    familyMembersContainer: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        minWidth: 0,
        overflow: 'hidden',
    },
    familySection: {
        flex: 1,
        minWidth: 0,
        paddingLeft: 10,
    },
    familySectionLabel: {
        fontSize: 9,
        fontFamily: fonts.heading,
        color: colors.textSecondary,
        textTransform: 'uppercase',
        marginTop: 4,
    },
    avatarScrollContent: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        paddingRight: 8,
    },
    avatarItem: {
        position: 'relative',
    },
    avatarLoadingOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        borderRadius: FAMILY_AVATAR_SIZE / 2,
        justifyContent: 'center',
        alignItems: 'center',
    },
    avatarBadge: {
        position: 'absolute',
        bottom: -3,
        right: -3,
        pointerEvents: 'none',
    },
    separator: {
        width: 1,
        height: '100%',
        backgroundColor: colors.background,
        marginHorizontal: 10,
    },
    checkAllInButton: {
        height: 35,
        width: 35,
        alignSelf: 'center',
        backgroundColor: colors.background,
        borderRadius: 8,
        justifyContent: 'center',
        alignItems: 'center',
        marginLeft: 8,
        flexShrink: 0,
    },
    noFamilyContainer: {
        flex: 1,
        paddingHorizontal: 15,
    },
    noFamilyText: {
        fontSize: 12,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        fontStyle: 'italic',
    },
    actionsSection: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-end',
        paddingHorizontal: 12,
        paddingVertical: 10,
        backgroundColor: colors.surface,
        borderTopWidth: 1,
        borderTopColor: colors.background,
        borderBottomLeftRadius: 10,
        borderBottomRightRadius: 10,
    },
    actionsRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
    },
    actionButton: {
        flexDirection: 'row', 
        alignItems: 'center', 
        gap: 2
    },
    actionText: {
        fontSize: 12,
        fontFamily: fonts.body,
        color: colors.primary,
    },
    actionTextSuccess: {
        fontSize: 12,
        fontFamily: fonts.body,
        color: colors.success,
    },
}));

export default React.memo(CheckinCardGrid);
